/**
 * Tracks which preference type icons/images loaded successfully.
 *
 * Belongs here: image availability state.
 * Does not belong here: API mapping.
 */
import { useMemo } from 'react'
import type { FundPreferenceType } from './setup/types'
import { getPreferenceTypeIconImageUrl } from './ui/getPreferenceTypeIcon'
import { useExpertReviewImageAvailabilityByUrl, useExpertReviewPreloadImages } from './useExpertReviewPreloadImages'
import { mfLogoBaseUrl } from '../../../../env'
import { ImageAvailability } from '../shared/types/common.types'

export function usePreferencesImages(preferenceTypesFromList: FundPreferenceType[]) {
  const preferenceTypeIconUrlById = useMemo(() => {
    const next: Record<string, string> = {}
    preferenceTypesFromList.forEach((preferenceType) => {
      const preferenceTypeId = preferenceType?.id ?? ''
      if (!preferenceTypeId) return
      const url = getPreferenceTypeIconImageUrl(preferenceTypeId)
      if (url) next[preferenceTypeId] = url
    })
    return next
  }, [preferenceTypesFromList])

  const preferenceTypeIconUrls = useMemo(
    () => Object.values(preferenceTypeIconUrlById),
    [preferenceTypeIconUrlById]
  )

  const availabilityByUrl = useExpertReviewImageAvailabilityByUrl(preferenceTypeIconUrls)

  const preferenceTypeIconAvailabilityById = useMemo(() => {
    const next: Record<string, ImageAvailability> = {}
    Object.entries(preferenceTypeIconUrlById).forEach(([preferenceTypeId, url]) => {
      next[preferenceTypeId] = availabilityByUrl[url] ?? ImageAvailability.UNKNOWN
    })
    return next
  }, [availabilityByUrl, preferenceTypeIconUrlById])

  const fundLogoUrls = useMemo(() => {
    const urls: string[] = []
    preferenceTypesFromList.forEach((preferenceType) => {
      const funds = preferenceType?.fundList ?? []
      funds.forEach((fund) => {
        const rawLogoPath = fund?.companyLogoUrl ?? ''
        if (!rawLogoPath) return
        urls.push(`${mfLogoBaseUrl}${rawLogoPath}`)
      })
    })
    return urls
  }, [preferenceTypesFromList])

  useExpertReviewPreloadImages(fundLogoUrls)

  return { preferenceTypeIconAvailabilityById }
}

